import React, { useState } from 'react';
import { Table, Button, Space, message, Modal, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/axios';

interface AdminPageListDto {
    id: string;
    title: string;
    slug: string;
    isActive: boolean;
    availableLanguages: string[];
}

export const PagesList: React.FC = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const { data, isLoading } = useQuery({
        queryKey: ['adminPages', page, pageSize],
        queryFn: async () => {
            const response = await apiClient.get('/admin/pages', {
                params: { page, pageSize }
            });
            return response.data;
        }
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            await apiClient.delete(`/admin/pages/${id}`);
        },
        onSuccess: () => {
            message.success('Page deleted successfully.');
            queryClient.invalidateQueries({ queryKey: ['adminPages'] });
        },
        onError: () => {
            message.error('Failed to delete page.');
        }
    });

    const handleDelete = (id: string) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this page?',
            content: 'This action cannot be undone.',
            okText: 'Yes, Delete',
            okType: 'danger',
            onOk: () => deleteMutation.mutate(id)
        });
    };

    const columns = [
        { title: 'Title (Primary)', dataIndex: 'title', key: 'title' },
        { title: 'Slug', dataIndex: 'slug', key: 'slug' },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Draft'}</Tag>
            )
        },
        {
            title: 'Translations',
            dataIndex: 'availableLanguages',
            key: 'availableLanguages',
            render: (langs: string[]) => (
                <Space size={[0, 8]} wrap>
                    {langs.map(l => <Tag key={l}>{l.toUpperCase()}</Tag>)}
                </Space>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: AdminPageListDto) => (
                <Space size="middle">
                    <Button icon={<EditOutlined />} onClick={() => navigate(`/admin/pages/edit/${record.id}`)} />
                    <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
                </Space>
            )
        }
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h2>Pages</h2>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/admin/pages/create')}>
                    Create Page
                </Button>
            </div>

            <Table
                dataSource={data?.data || []}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: data?.totalRecords || 0,
                    onChange: (p, s) => { setPage(p); setPageSize(s); }
                }}
            />
        </div>
    );
};
